import { PDFDocument, rgb, PageSizes, StandardFonts } from 'pdf-lib';
import {
  getOverAllScore,
  getOverAllGrade,
  decideCurrency,
  getTwoDecimal,
  arrangeSector,
  removeWsPrefix,
} from 'utils/helpers';
import { tiltLabels } from 'utils/constants';

const parseDate = (portfolio, short) => {
  const init = portfolio.init_date.split('-');
  const end = portfolio.end_date.split('-');
  const months = {
    '01': 'January',
    '02': 'February',
    '03': 'March',
    '04': 'April',
    '05': 'May',
    '06': 'June',
    '07': 'July',
    '08': 'August',
    '09': 'September',
    10: 'October',
    11: 'November',
    12: 'December',
  };

  return short
    ? `${init[1]}/${init[0]} - ${end[1]}/${end[0]}`
    : `${months[init[1]]} ${init[0]} to ${months[end[1]]} ${end[0]}`;
};

const getSubHeader = (data) =>
  `${parseDate(data.portfolio)} (inclusive) in ${decideCurrency(
    data,
    localStorage.getItem('user_region') || 'CA'
  )}`;

const getPercent = (num) => (!num ? '-' : `${(num * 100).toFixed(2)}%`);

const formatNum = (num) => (!num ? '-' : num.toFixed(2));
const getDownside = (num) => (num ? `${(num * 100).toFixed(2)}%` : '0%');

const isLinked = (data) => {
  if (!data || !data.portfolio) {
    return false;
  }

  if (
    data.portfolio.portfolio_type === 'custom' ||
    data.portfolio.portfolio_type === 'quantified'
  ) {
    return true;
  }

  return data.portfolio.is_linked;
};

const getName = (ticker, data) => {
  const { tickersValid } = data;

  return isLinked(data)
    ? tickersValid[ticker].name
    : tickersValid[ticker].wsName;
};

const getDifference = (portfolioVal, benchmarkVal) => {
  // portfolio or benchmark values for each sector could be null
  portfolioVal = portfolioVal || 0;
  benchmarkVal = benchmarkVal || 0;

  return portfolioVal - benchmarkVal;
};

const getTableData = (data) => {
  const arranged = arrangeSector(data);
  const result = Object.keys(arranged).reduce((total, key) => {
    total.push({
      ticker: key,
      value: data[key],
    });

    return total;
  }, []);
  return result;
};

export async function generateOverviewPDF(analytics) {
  const { esgToggle, income, benchmark, data, portfolioHistoryCompareto, esg } =
    analytics;
  const { advisor_fee } = data.paramsPassed;

  const rebalancingFrequency = data.paramsAnalyzed.rebalance_interval || 1;

  const pdfDoc = await PDFDocument.create();
  let page = pdfDoc.addPage(PageSizes.Letter);
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

  const fontSizeTitle = 12;
  const fontSizeSubtitle = 10;
  const fontSizeBody = 8;

  const spacing = 12;
  const { width, height } = page.getSize();
  let y = height - 50;

  const fileName =
    data.portfolio.account && Object.keys(data.portfolio.account).length
      ? data.portfolio.account.user_institution
      : data.portfolio.name
      ? removeWsPrefix(data.portfolio.name)
      : 'Portfolio';
  page.drawText('Portfolio Scorecard', {
    x: 50,
    y,
    size: fontSizeTitle,
    color: rgb(0, 0, 0),
    font: boldFont,
  });

  y -= spacing;

  page.drawText(`${fileName}`, {
    x: 50,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font,
  });

  // Overview Page:
  const sortedHoldings = data.tickersOriginal.sort(
    (a, b) => b.weight - a.weight
  );

  y -= 2 * spacing;
  page.drawText('Overview', {
    x: 50,
    y,
    size: fontSizeTitle,
    color: rgb(0, 0, 0),
    font: boldFont,
  });

  y -= spacing;

  page.drawText(
    `Portfolio Overview ${
      sortedHoldings.length > 20 ? ' - Top 20 holdings' : ''
    }`,
    {
      x: 50,
      y,
      size: fontSizeSubtitle,
      color: rgb(0, 0, 0),
      font: boldFont,
    }
  );

  y -= 2 * spacing;
  let holdingsY = y;

  for (let i = 0; i < Math.min(sortedHoldings.length, 20); i += 1) {
    page.drawText(`${sortedHoldings[i].ticker}`, {
      x: i < 10 ? 50 : width / 2,
      y: holdingsY,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    });
    page.drawText(`${sortedHoldings[i].name}`, {
      x: i < 10 ? 50 : width / 2,
      y: holdingsY - spacing,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    });
    page.drawText(`${(sortedHoldings[i].weight * 100).toFixed(2)}%`, {
      x: i < 10 ? 250 : width / 2 + 200,
      y: holdingsY,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    });
    page.drawRectangle({
      x: i < 10 ? 40 : width / 2 - 10,
      y: holdingsY - 1.5 * spacing,
      width: width / 2 - 50,
      height: 2.5 * spacing,
      borderColor: rgb(0, 0, 0),
      borderWidth: 0.5,
    });

    if (i === 9) {
      holdingsY = y;
    } else {
      holdingsY -= 2.5 * spacing;
    }
  }

  y = holdingsY - 0.5 * spacing;
  page.drawText(
    `Overall Score ${getOverAllScore({
      income,
      benchmark,
      esgToggle,
      data,
      esg,
    })} ${getOverAllGrade({
      income,
      benchmark,
      esgToggle,
      data,
      esg,
    })}`,
    {
      x: 50,
      y,
      size: fontSizeSubtitle,
      color: rgb(0, 0, 0),
      font: boldFont,
    }
  );

  y -= spacing;
  page.drawText(
    `Benchmark: ${
      portfolioHistoryCompareto === 'market'
        ? data.cumrets.market[`${benchmark}Desc`]
        : data.cumrets.benchmark[`${benchmark}Desc`]
    }`,
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );
  y -= spacing;
  page.drawText(
    `Rebalancing Frequency: ${
      rebalancingFrequency === 1
        ? 'Monthly'
        : rebalancingFrequency === 3
        ? 'Quarterly'
        : 'Annually'
    }`,
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );

  // Historical Performance Page:

  y -= 2 * spacing;
  page.drawText('Crisis Period Returns', {
    x: 50,
    y,
    size: fontSizeSubtitle,
    color: rgb(0, 0, 0),
    font: boldFont,
  });

  let historyTableX = 150;
  y -= spacing;

  if (Object.keys(data.crisisRets['Financial Crisis'].rets).length !== 0) {
    page.drawText('Financial Crisis', {
      x: historyTableX,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    });
    page.drawText('(2007-12 to 2009-05)', {
      x: historyTableX,
      y: y - spacing,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    });
    historyTableX += 100;
  }

  if (Object.keys(data.crisisRets['Oil Price Shock'].rets).length !== 0) {
    page.drawText('Oil Price Shock', {
      x: historyTableX,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    });
    page.drawText('(2014-06 to 2016-01)', {
      x: historyTableX,
      y: y - spacing,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    });
    historyTableX += 100;
  }

  if (Object.keys(data.crisisRets['Covid-19 Pandemic'].rets).length !== 0) {
    page.drawText('Covid-19 Pandemic', {
      x: historyTableX,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    });
    page.drawText('(2020-02 to 2021-06)', {
      x: historyTableX,
      y: y - spacing,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    });
    historyTableX += 100;
  }

  if (Object.keys(data.crisisRets['Ukraine War'].rets).length !== 0) {
    page.drawText('Ukraine War Onset', {
      x: historyTableX,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    });
    page.drawText('(2022-03 to 2022-09)', {
      x: historyTableX,
      y: y - spacing,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    });
  }

  page.drawRectangle({
    x: 40,
    y: y - 1.75 * spacing,
    width: width - 80,
    height: 0,
    borderColor: rgb(0, 0, 0),
    borderWidth: 0.5,
  });

  historyTableX = 50;

  y -= 3 * spacing;
  page.drawText('My Portfolio', {
    x: historyTableX,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font,
  });
  historyTableX += 100;

  if (Object.keys(data.crisisRets['Financial Crisis'].rets).length !== 0) {
    page.drawText(
      `${(data.crisisRets['Financial Crisis'].rets.portfolio * 100).toFixed(
        2
      )}%`,
      {
        x: historyTableX,
        y,
        size: fontSizeBody,
        color: rgb(0, 0, 0),
        font,
      }
    );
    historyTableX += 100;
  }

  if (Object.keys(data.crisisRets['Oil Price Shock'].rets).length !== 0) {
    page.drawText(
      `${(data.crisisRets['Oil Price Shock'].rets.portfolio * 100).toFixed(
        2
      )}%`,
      {
        x: historyTableX,
        y,
        size: fontSizeBody,
        color: rgb(0, 0, 0),
        font,
      }
    );
    historyTableX += 100;
  }

  if (Object.keys(data.crisisRets['Covid-19 Pandemic'].rets).length !== 0) {
    page.drawText(
      `${(data.crisisRets['Covid-19 Pandemic'].rets.portfolio * 100).toFixed(
        2
      )}%`,
      {
        x: historyTableX,
        y,
        size: fontSizeBody,
        color: rgb(0, 0, 0),
        font,
      }
    );
    historyTableX += 100;
  }

  if (Object.keys(data.crisisRets['Ukraine War'].rets).length !== 0) {
    page.drawText(
      `${(data.crisisRets['Ukraine War'].rets.portfolio * 100).toFixed(2)}%`,
      {
        x: historyTableX,
        y,
        size: fontSizeBody,
        color: rgb(0, 0, 0),
        font,
      }
    );
    historyTableX += 100;
  }

  page.drawRectangle({
    x: 40,
    y: y - 0.75 * spacing,
    width: width - 80,
    height: 0,
    borderColor: rgb(0, 0, 0),
    borderWidth: 0.5,
  });

  y -= 2 * spacing;
  historyTableX = 50;
  page.drawText(
    `${
      portfolioHistoryCompareto[0].toUpperCase() +
      portfolioHistoryCompareto.slice(1)
    }`,
    {
      x: historyTableX,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );
  historyTableX += 100;

  if (Object.keys(data.crisisRets['Financial Crisis'].rets).length !== 0) {
    page.drawText(
      `${
        data.crisisRets['Financial Crisis'].rets[portfolioHistoryCompareto][
          benchmark
        ] * 100
      }%`,
      {
        x: historyTableX,
        y,
        size: fontSizeBody,
        color: rgb(0, 0, 0),
        font,
      }
    );
    historyTableX += 100;
  }

  if (Object.keys(data.crisisRets['Oil Price Shock'].rets).length !== 0) {
    page.drawText(
      `${
        data.crisisRets['Oil Price Shock'].rets[portfolioHistoryCompareto][
          benchmark
        ] * 100
      }%`,
      {
        x: historyTableX,
        y,
        size: fontSizeBody,
        color: rgb(0, 0, 0),
        font,
      }
    );
    historyTableX += 100;
  }

  if (Object.keys(data.crisisRets['Covid-19 Pandemic'].rets).length !== 0) {
    page.drawText(
      `${
        data.crisisRets['Covid-19 Pandemic'].rets[portfolioHistoryCompareto][
          benchmark
        ] * 100
      }%`,
      {
        x: historyTableX,
        y,
        size: fontSizeBody,
        color: rgb(0, 0, 0),
        font,
      }
    );
    historyTableX += 100;
  }

  if (Object.keys(data.crisisRets['Ukraine War'].rets).length !== 0) {
    page.drawText(
      `${
        data.crisisRets['Ukraine War'].rets[portfolioHistoryCompareto][
          benchmark
        ] * 100
      }%`,
      {
        x: historyTableX,
        y,
        size: fontSizeBody,
        color: rgb(0, 0, 0),
        font,
      }
    );
    historyTableX += 100;
  }

  // Performance Page:

  page = pdfDoc.addPage(PageSizes.Letter);
  y = height - 50;

  page.drawText(
    `Performance: ${data.score.Performance[benchmark][0]}% ${data.score.Performance[benchmark][1]}`,
    {
      x: 50,
      y,
      size: fontSizeTitle,
      color: rgb(0, 0, 0),
      font: boldFont,
    }
  );

  y -= spacing;
  page.drawText(
    'The information below provides a snapshot of how well your portfolio has been doing. You will find the traditional measures, such as',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );

  y -= spacing;
  page.drawText(
    'annualized returns for different investment horizons. Most importantly, we show you your portfolio’s risk-adjusted ratios (Sharpe',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );

  y -= spacing;
  page.drawText(
    "Ratio, Roy's Safety First Criterion, and Sortino Ratio), which tell you how much risk was taken to generate the returns.",
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );

  y -= 2 * spacing;
  page.drawText(
    'As long as you invest in securities that involve some level of risk - however high or low – the risk adjusted ratios are more',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );

  y -= spacing;
  page.drawText(
    'appropriate measures of performance, because the returns are not guaranteed. Needless to say, the higher the ratios, especially',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );

  y -= spacing;
  page.drawText("relative to your portfolio's benchmark, the better.", {
    x: 50,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font,
  });

  y -= 2 * spacing;
  page.drawText('Key Statistics', {
    x: 50,
    y,
    size: fontSizeSubtitle,
    color: rgb(0, 0, 0),
    font: boldFont,
  });

  y -= spacing;
  page.drawText('Based on the current composition of your portfolio', {
    x: 50,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font,
  });

  y -= spacing;
  page.drawText(`${getSubHeader(data)}`, {
    x: 50,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font,
  });

  y -= spacing;
  page.drawText('Annualized Return', {
    x: 150,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font: boldFont,
  });

  page.drawText('Sharpe Ratio', {
    x: 250,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font: boldFont,
  });

  page.drawText("Roy's Safety First Criterion", {
    x: 350,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font: boldFont,
  });

  page.drawText('Sortino Ratio', {
    x: 500,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font: boldFont,
  });
  page.drawRectangle({
    x: 40,
    y: y - 0.75 * spacing,
    width: width - 80,
    height: 0,
    borderColor: rgb(0, 0, 0),
    borderWidth: 0.5,
  });

  y -= 2 * spacing;
  page.drawText('My Portfolio', {
    x: 50,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font,
  });

  page.drawText(`${getPercent(data.fratios.portfolio.annualizedRet)}`, {
    x: 150,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font,
  });

  page.drawText(`${formatNum(data.fratios.portfolio.sharpe)}`, {
    x: 250,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font,
  });

  page.drawText(`${formatNum(data.fratios.portfolio.roys)}`, {
    x: 350,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font,
  });

  page.drawText(`${formatNum(data.fratios.portfolio.sortino)}`, {
    x: 500,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font,
  });

  page.drawRectangle({
    x: 40,
    y: y - 0.75 * spacing,
    width: width - 80,
    height: 0,
    borderColor: rgb(0, 0, 0),
    borderWidth: 0.5,
  });

  y -= 2 * spacing;
  page.drawText(`${data.fratios[benchmark].name}`, {
    x: 50,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font,
  });

  page.drawText(`${getPercent(data.fratios[benchmark].annualizedRet)}`, {
    x: 150,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font,
  });

  page.drawText(`${formatNum(data.fratios[benchmark].sharpe)}`, {
    x: 250,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font,
  });

  page.drawText(`${formatNum(data.fratios[benchmark].roys)}`, {
    x: 350,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font,
  });

  page.drawText(`${formatNum(data.fratios[benchmark].sortino)}`, {
    x: 500,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font,
  });

  y -= 2 * spacing;
  page.drawText('Annualized Returns', {
    x: 50,
    y,
    size: fontSizeSubtitle,
    color: rgb(0, 0, 0),
    font: boldFont,
  });

  y -= spacing;
  page.drawText(`${getSubHeader(data)}`, {
    x: 50,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font,
  });

  y -= spacing;
  page.drawText('YTD', {
    x: 150,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font: boldFont,
  });

  page.drawText('1-year', {
    x: 225,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font: boldFont,
  });

  page.drawText('3-year', {
    x: 300,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font: boldFont,
  });

  page.drawText('5-year', {
    x: 375,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font: boldFont,
  });

  page.drawText('7-year', {
    x: 450,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font: boldFont,
  });
  page.drawText('10-year', {
    x: 525,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font: boldFont,
  });

  page.drawRectangle({
    x: 40,
    y: y - 0.75 * spacing,
    width: width - 80,
    height: 0,
    borderColor: rgb(0, 0, 0),
    borderWidth: 0.5,
  });

  y -= 2 * spacing;
  page.drawText('My Portfolio', {
    x: 50,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font,
  });

  page.drawText(`${getPercent(data.fratios.portfolio.retYTD)}`, {
    x: 150,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font,
  });

  page.drawText(`${getPercent(data.fratios.portfolio.ret1)}`, {
    x: 225,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font,
  });

  page.drawText(`${getPercent(data.fratios.portfolio.ret3)}`, {
    x: 300,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font,
  });

  page.drawText(`${getPercent(data.fratios.portfolio.ret5)}`, {
    x: 375,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font,
  });

  page.drawText(`${getPercent(data.fratios.portfolio.ret7)}`, {
    x: 450,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font,
  });

  page.drawText(`${getPercent(data.fratios.portfolio.ret10)}`, {
    x: 525,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font,
  });

  page.drawRectangle({
    x: 40,
    y: y - 0.75 * spacing,
    width: width - 80,
    height: 0,
    borderColor: rgb(0, 0, 0),
    borderWidth: 0.5,
  });

  y -= 2 * spacing;
  page.drawText(`${data.fratios[benchmark].name}`, {
    x: 50,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font,
  });

  page.drawText(`${getPercent(data.fratios[benchmark].retYTD)}`, {
    x: 150,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font,
  });

  page.drawText(`${getPercent(data.fratios[benchmark].ret1)}`, {
    x: 225,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font,
  });

  page.drawText(`${getPercent(data.fratios[benchmark].ret3)}`, {
    x: 300,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font,
  });

  page.drawText(`${getPercent(data.fratios[benchmark].ret5)}`, {
    x: 375,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font,
  });

  page.drawText(`${getPercent(data.fratios[benchmark].ret7)}`, {
    x: 450,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font,
  });

  page.drawText(`${getPercent(data.fratios[benchmark].ret10)}`, {
    x: 525,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font,
  });

  // Downside Page:

  y -= 2 * spacing;
  page.drawText(
    `Downside: ${data.score.Volatility[benchmark][0]}% ${data.score.Volatility[benchmark][1]}`,
    {
      x: 50,
      y,
      size: fontSizeTitle,
      color: rgb(0, 0, 0),
      font: boldFont,
    }
  );

  y -= spacing;
  page.drawText(
    "Downside protection speaks to your portfolio's ability to limit losses when there is a market downturn.",
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );

  y -= 2 * spacing;
  page.drawText(
    'In portfolio management, the traditional measure of risk (or volatility) is the standard deviation of a portfolio’s historical returns.',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );

  y -= spacing;
  page.drawText(
    "It is a measure of the portfolio's total risk. It is reported as an annualized percentage for easy comparison across portfolios and time periods.",
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );

  y -= 2 * spacing;
  page.drawText(
    'It is important to note that total risk has an upside and a downside. Over time, portfolio returns will fluctuate above the average (or your target return),',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );

  y -= spacing;
  page.drawText(
    'as well as below. It is human nature and often rational to be more concerned about downside risk than upside risk.',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );

  y -= 2 * spacing;
  page.drawText('Key Statistics', {
    x: 50,
    y,
    size: fontSizeSubtitle,
    color: rgb(0, 0, 0),
    font: boldFont,
  });

  y -= spacing;
  page.drawText('Based on the current composition of your portfolio', {
    x: 50,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font,
  });

  y -= spacing;
  page.drawText(`${getSubHeader(data)}`, {
    x: 50,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font,
  });

  y -= spacing;
  page.drawText('Total Risk', {
    x: 150,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font: boldFont,
  });
  page.drawText('Downside Risk', {
    x: 250,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font: boldFont,
  });

  page.drawText('Maximum Drawdown', {
    x: 350,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font: boldFont,
  });

  page.drawText('Downside Capture', {
    x: 450,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font: boldFont,
  });
  page.drawRectangle({
    x: 40,
    y: y - 0.75 * spacing,
    width: width - 80,
    height: 0,
    borderColor: rgb(0, 0, 0),
    borderWidth: 0.5,
  });

  y -= 2 * spacing;
  page.drawText('My Porfolio', {
    x: 50,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font,
  });
  page.drawText(`${getPercent(data.fratios.portfolio.stdDev)}`, {
    x: 150,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font,
  });

  page.drawText(`${getDownside(data.fratios.portfolio.dsr)}`, {
    x: 250,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font,
  });

  page.drawText(`${getDownside(data.fratios.portfolio.maxDd)}`, {
    x: 350,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font,
  });

  page.drawText(`${getPercent(data.fratios.portfolio.dscapture[benchmark])}`, {
    x: 450,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font,
  });
  page.drawRectangle({
    x: 40,
    y: y - 0.75 * spacing,
    width: width - 80,
    height: 0,
    borderColor: rgb(0, 0, 0),
    borderWidth: 0.5,
  });

  y -= 2 * spacing;
  page.drawText(`${data.fratios[benchmark].name}`, {
    x: 50,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font,
  });
  page.drawText(`${getPercent(data.fratios[benchmark].stdDev)}`, {
    x: 150,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font,
  });

  page.drawText(`${getPercent(data.fratios[benchmark].dsr)}`, {
    x: 250,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font,
  });

  page.drawText(`${getPercent(data.fratios[benchmark].maxDd)}`, {
    x: 350,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font,
  });

  page.drawText(`${getPercent(data.fratios[benchmark].dscapture)}`, {
    x: 450,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font,
  });

  // Fees Page:
  page = pdfDoc.addPage(PageSizes.Letter);
  y = height - 50;

  page.drawText(`Fees: ${data.score.Fees[0]}% ${data.score.Fees[1]}`, {
    x: 50,
    y,
    size: fontSizeTitle,
    color: rgb(0, 0, 0),
    font: boldFont,
  });

  y -= spacing;
  page.drawText(
    'In this segment, we show you your portfolio’s average Management Expense Ratio (MER). MER is a fee charged by ETFs and mutual funds.',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );
  y -= spacing;
  page.drawText(
    'Depending on the series (e.g., A), it may include a trailing commission that compensates your advisor. Note that any',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );

  y -= spacing;
  page.drawText(
    'additional advisory or trading fees you may incur are not included. MER is expressed as a percentage of your fund value.',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );

  y -= 2 * spacing;
  page.drawText(
    'Needless to say, the lower the MER of your portfolio, the higher score it will receive.',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );

  y -= 2 * spacing;
  page.drawText('Fees In This Portfolio', {
    x: 50,
    y,
    size: fontSizeSubtitle,
    color: rgb(0, 0, 0),
    font: boldFont,
  });

  y -= spacing;
  page.drawText('Asset', {
    x: 50,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font: boldFont,
  });
  page.drawText('Weight', {
    x: 450,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font: boldFont,
  });

  page.drawText('MER', {
    x: 500,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font: boldFont,
  });
  data.tickersOriginal.forEach((row) => {
    if (row.name) {
      page.drawRectangle({
        x: 40,
        y: y - 0.5 * spacing,
        width: width - 80,
        height: 0,
        borderColor: rgb(0, 0, 0),
        borderWidth: 0.5,
      });

      y -= 1.5 * spacing;

      page.drawText(isLinked(data) ? row.name : row.wsName, {
        x: 50,
        y,
        size: fontSizeBody,
        color: rgb(0, 0, 0),
        font,
      });
      page.drawText(`${Math.round(row.weight * 10000) / 100}%`, {
        x: 450,
        y,
        size: fontSizeBody,
        color: rgb(0, 0, 0),
        font,
      });
      page.drawText(`${Math.round(row.mer * 10000) / 100}%`, {
        x: 500,
        y,
        size: fontSizeBody,
        color: rgb(0, 0, 0),
        font,
      });
      if (y < 50) {
        page = pdfDoc.addPage(PageSizes.Letter);
        y = height - 50;
      }
    }
  });

  y -= 2 * spacing;
  page.drawText(`Weighted Average MER: ${(data.portMER * 100).toFixed(2)}%`, {
    x: 50,
    y,
    size: fontSizeSubtitle,
    color: rgb(0, 0, 0),
    font,
  });

  if (advisor_fee && advisor_fee !== 0) {
    y -= spacing;
    page.drawText(`+ ${((advisor_fee / 100) * 100).toFixed(2)}%`, {
      x: 50,
      y,
      size: fontSizeSubtitle,
      color: rgb(0, 0, 0),
      font,
    });
    page.drawText('Advisor Fee', {
      x: 50,
      y: y - spacing,
      size: fontSizeSubtitle,
      color: rgb(0, 0, 0),
      font,
    });
    page.drawText(
      `= ${((data.portMER + advisor_fee / 100) * 100).toFixed(2)}%`,
      {
        x: 250,
        y,
        size: fontSizeSubtitle,
        color: rgb(0, 0, 0),
        font,
      }
    );
    page.drawText('Total fees', {
      x: 250,
      y: y - spacing,
      size: fontSizeSubtitle,
      color: rgb(0, 0, 0),
      font,
    });
  }

  // Income Page:

  page = pdfDoc.addPage(PageSizes.Letter);
  y = height - 50;
  page.drawText(`Income: ${data.score.Income[0]}% ${data.score.Income[1]}`, {
    x: 50,
    y,
    size: fontSizeTitle,
    color: rgb(0, 0, 0),
    font: boldFont,
  });

  y -= spacing;
  page.drawText(
    'Income refers to cash dividends from a stock, or cash distributions from an ETF or a mutual fund.',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );

  y -= 2 * spacing;
  page.drawText(
    'Whether you desire a high income score depends on your saving objective. If you are investing for the long term, or if you are not',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );

  y -= spacing;
  page.drawText(
    'dependent on your portfolio for everyday expenses, a high income portfolio is not necessary.',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );

  y -= 2 * spacing;
  page.drawText('Portfolio Distribution Yield', {
    x: 50,
    y,
    size: fontSizeSubtitle,
    color: rgb(0, 0, 0),
    font: boldFont,
  });

  y -= spacing;
  page.drawText('Asset', {
    x: 50,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font: boldFont,
  });
  page.drawText('Weight', {
    x: 450,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font: boldFont,
  });

  page.drawText('Yield', {
    x: 500,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font: boldFont,
  });

  data.tickersOriginal.forEach((row) => {
    if (row.name) {
      page.drawRectangle({
        x: 40,
        y: y - 0.5 * spacing,
        width: width - 80,
        height: 0,
        borderColor: rgb(0, 0, 0),
        borderWidth: 0.5,
      });

      y -= 1.5 * spacing;

      page.drawText(isLinked(data) ? row.name : row.wsName, {
        x: 50,
        y,
        size: fontSizeBody,
        color: rgb(0, 0, 0),
        font,
      });
      page.drawText(`${Math.round(row.weight * 10000) / 100}%`, {
        x: 450,
        y,
        size: fontSizeBody,
        color: rgb(0, 0, 0),
        font,
      });
      page.drawText(`${Math.round(row.dist_yield * 10000) / 100}%`, {
        x: 500,
        y,
        size: fontSizeBody,
        color: rgb(0, 0, 0),
        font,
      });
      if (y < 50) {
        page = pdfDoc.addPage(PageSizes.Letter);
        y = height - 50;
      }
    }
  });

  y -= 2 * spacing;
  page.drawText(
    `Weighted Average Yield: ${(data.portYield * 100).toFixed(2)}%`,
    {
      x: 50,
      y,
      size: fontSizeSubtitle,
      color: rgb(0, 0, 0),
      font,
    }
  );

  // Diversification Page:

  const parsedData = Object.keys(data.corr).reduce((total, key) => {
    Object.keys(data.corr[key]).forEach((key2) => {
      if (
        data.corr[key][key2] &&
        data.corr[key][key2] < 1 &&
        data.corr[key][key2] >= 0.7
      ) {
        if (!total[key]) {
          total[key] = [
            {
              name: data.tickersValid[key].name,
              ticker: key,
              matchTicker: key2,
              matchName: data.tickersValid[key2].name,
              corr: data.corr[key][key2],
              rowSpan: 1,
            },
          ];
        } else {
          total[key][0].rowSpan += 1;
          total[key].push({
            name: data.tickersValid[key].name,
            ticker: key,
            matchTicker: key2,
            matchName: data.tickersValid[key2].name,
            corr: data.corr[key][key2],
            exclude: true,
          });
        }
      }
    });

    return total;
  }, {});

  const filtered = Object.values(parsedData).reduce((total, value, index) => {
    if (value.length === 1) {
      total.push({ key: index, item: value[0] });
    } else {
      value.forEach((item, idx) => total.push({ key: `${index}${idx}`, item }));
    }

    return total;
  }, []);

  page = pdfDoc.addPage(PageSizes.Letter);
  y = height - 50;
  page.drawText(
    `Diversification: ${data.score.Diversification[0]}% ${data.score.Diversification[1]}`,
    {
      x: 50,
      y,
      size: fontSizeTitle,
      color: rgb(0, 0, 0),
      font: boldFont,
    }
  );

  y -= 2 * spacing;
  page.drawText('Should you give up a free lunch?', {
    x: 50,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font,
  });
  y -= 2 * spacing;
  page.drawText(
    'The answer is a resounding NO when it comes to your investments. By diversifying properly and choosing assets that do not move together in',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );

  y -= spacing;
  page.drawText(
    'locked steps, you can reduce the amount of risk in your portfolio without sacrificing returns.',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );

  y -= 2 * spacing;
  page.drawText(
    'Be aware that merely adding more assets to your portfolio does not necessarily mean that you are diversifying effectively. For example, if all of your assets',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );

  y -= spacing;
  page.drawText(
    'are exposed to the same risk factor, then the diversification is superficial. It is important to diversify across different types of risk exposures.',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );

  y -= 2 * spacing;
  page.drawText('Correlation Matrix', {
    x: 50,
    y,
    size: fontSizeSubtitle,
    color: rgb(0, 0, 0),
    font,
  });

  y -= spacing;
  page.drawText(`${parseDate(data.portfolio)} (inclusive)`, {
    x: 50,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font,
  });

  y -= spacing;
  page.drawText(
    `TOTAL PORTFOLIO WEIGHTED AVERAGE CORRELATION: ${
      Math.round(data.ipc * 100) / 100
    }`,
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );
  y -= spacing;
  page.drawText('Asset pairs with correlation greater than or equal to 0.70', {
    x: 50,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font: boldFont,
  });

  y -= spacing;
  page.drawText('Asset', {
    x: 50,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font: boldFont,
  });

  page.drawText('Asset', {
    x: width / 2,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font: boldFont,
  });

  page.drawText('Correlation', {
    x: 500,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font: boldFont,
  });
  page.drawRectangle({
    x: 40,
    y: y - 0.75 * spacing,
    width: width - 80,
    height: 0,
    borderColor: rgb(0, 0, 0),
    borderWidth: 0.5,
  });

  y -= 2 * spacing;

  filtered.forEach((item) => {
    page.drawRectangle({
      x: 40,
      y: y - 1.75 * spacing,
      width: width - 80,
      height: 0,
      borderColor: rgb(0, 0, 0),
      borderWidth: 0.5,
    });

    page.drawText(`${item.item.ticker}`, {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    });

    page.drawText(`${item.item.name}`, {
      x: 50,
      y: y - spacing,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    });

    page.drawText(`${item.item.matchTicker}`, {
      x: width / 2,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    });

    page.drawText(`${item.item.matchName}`, {
      x: width / 2,
      y: y - spacing,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    });

    page.drawText(`${Math.round(item.item.corr * 100) / 100}`, {
      x: 500,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    });
    y -= 3 * spacing;
    if (y < 50) {
      page = pdfDoc.addPage(PageSizes.Letter);
      y = height - 50;
    }
  });

  // Asset Class Page:

  const assetAllocation = Object.values(data.tickersValid).reduce(
    (total, value) => {
      if (total[value.class]) {
        total[value.class] += value.weight;
      } else {
        total[value.class] = value.weight;
      }

      return total;
    },
    {}
  );

  const assetAllocationClass = {
    Alternative:
      'Alternative' in data.assetAllocation
        ? data.assetAllocation.Alternative
        : 0,
    Equity: 'Equity' in data.assetAllocation ? data.assetAllocation.Equity : 0,
    'Fixed Income':
      'Fixed Income' in data.assetAllocation
        ? data.assetAllocation['Fixed Income']
        : 0,
    'Misc.': 'Misc' in data.assetAllocation ? data.assetAllocation.Misc : 0,
    Cash: 'Cash' in data.assetAllocation ? data.assetAllocation.Cash : 0,
  };
  const riskContClass = {
    Alternative:
      'Alternative' in data.riskCont.class
        ? data.riskCont.class.Alternative
        : 0,
    Equity: 'Equity' in data.riskCont.class ? data.riskCont.class.Equity : 0,
    'Fixed Income':
      'Fixed Income' in data.riskCont.class
        ? data.riskCont.class['Fixed Income']
        : 0,
    Cash: 'Cash' in data.riskCont.class ? data.riskCont.class.Cash : 0,
    Other: 'Other' in data.riskCont.class ? data.riskCont.class.Other : 0,
  };
  const customOrder = [
    'Alternative',
    'Equity',
    'Fixed Income',
    'Cash',
    'Misc',
    'Other',
  ];
  const sortedData = customOrder.map((key) => ({
    ticker: key,
    assetAllocation: assetAllocation[key] || 0,
    riskCont: riskContClass[key] || 0,
    retCont: data.retCont.class[key] || 0,
  }));
  const customSortOrder = (a, b) =>
    customOrder.indexOf(a.ticker) - customOrder.indexOf(b.ticker);

  sortedData.sort(customSortOrder);
  const filteredAssetAllocationClass = Object.entries(assetAllocationClass)
    // eslint-disable-next-line no-unused-vars
    .filter(([key, value]) => value !== 0)
    .reduce((obj, [key, value]) => {
      obj[key] = value;
      return obj;
    }, {});

  const filteredData = sortedData.filter(
    (row) => !Object.values(row).includes('Misc')
  );

  page = pdfDoc.addPage(PageSizes.Letter);
  y = height - 50;
  page.drawText('Asset Class Analysis', {
    x: 50,
    y,
    size: fontSizeTitle,
    color: rgb(0, 0, 0),
    font: boldFont,
  });

  y -= spacing;
  page.drawText('Net Asset Allocation', {
    x: 50,
    y,
    size: fontSizeSubtitle,
    color: rgb(0, 0, 0),
    font: boldFont,
  });

  y -= spacing;
  page.drawText(
    'Your portfolio’s net asset allocation reflects the asset allocation',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );
  y -= spacing;
  page.drawText('of any balanced funds/funds of funds that you may have.', {
    x: 50,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font,
  });

  y -= 2 * spacing;
  page.drawText(
    'For example, if you put 50% of your money in a 60/40 fund and',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );
  y -= spacing;
  page.drawText(
    '50% in an 80/20 fund, then your net asset allocation is 70/30.',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );

  y -= 2 * spacing;
  page.drawText(
    'Cash is non-interest bearing cash in your account if you chose to analyze with cash.',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );

  y -= 2 * spacing;
  page.drawText('Miscellaneous includes funds of funds, convertibles assets', {
    x: 50,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font,
  });
  y -= spacing;
  page.drawText(
    '(excluding bonds and preferred shares), derivative contracts and others.',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );

  page.drawText('Holdings by Asset Class', {
    x: 400,
    y: y + 6 * spacing,
    size: fontSizeSubtitle,
    color: rgb(0, 0, 0),
    font: boldFont,
  });

  y -= spacing;
  page.drawText('Asset Class', {
    x: 400,
    y: y + 5 * spacing,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font: boldFont,
  });
  page.drawText('Allocation', {
    x: 500,
    y: y + 5 * spacing,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font: boldFont,
  });

  Object.keys(filteredAssetAllocationClass).forEach((assetClass, i) => {
    page.drawText(`${assetClass}`, {
      x: 400,
      y: y + (4 - i) * spacing,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    });
    page.drawText(
      `${Math.round(filteredAssetAllocationClass[assetClass] * 10000) / 100}%`,
      {
        x: 500,
        y: y + (4 - i) * spacing,
        size: fontSizeBody,
        color: rgb(0, 0, 0),
        font,
      }
    );
  });

  y -= 2 * spacing;
  page.drawText('Risk Contribution by Asset Class', {
    x: 50,
    y,
    size: fontSizeSubtitle,
    color: rgb(0, 0, 0),
    font: boldFont,
  });

  y -= spacing;
  page.drawText(
    'If you have balanced/asset allocation funds: please note that they are classified as "Other" by data vendors. Unfortunately, we are not able',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );
  y -= spacing;
  page.drawText(
    'to estimate the risk contribution of the sub-portfolios within a fund (e.g., the equity portion of a balanced fund) because we do not have',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );
  y -= spacing;
  page.drawText(
    'separate sub-portfolio returns data. In this case, your asset allocation ring chart below will be different from the one above.',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );

  y -= spacing;
  page.drawText('Class', {
    x: 50,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font: boldFont,
  });
  page.drawText('Allocation', {
    x: 250,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font: boldFont,
  });
  page.drawText('Risk Contribution', {
    x: 350,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font: boldFont,
  });
  page.drawText('Return Contribution', {
    x: 450,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font: boldFont,
  });

  filteredData.forEach((assetClass) => {
    page.drawRectangle({
      x: 40,
      y: y - 0.5 * spacing,
      width: width - 80,
      height: 0,
      borderColor: rgb(0, 0, 0),
      borderWidth: 0.5,
    });

    y -= 1.5 * spacing;
    page.drawText(
      assetClass.ticker === 'Other'
        ? 'Asset Allocation/Balanced Funds'
        : `${assetClass.ticker}`,
      {
        x: 50,
        y,
        size: fontSizeBody,
        color: rgb(0, 0, 0),
        font,
      }
    );
    page.drawText(`${Math.round(assetClass.assetAllocation * 10000) / 100}%`, {
      x: 250,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    });
    page.drawText(`${Math.round(assetClass.riskCont * 10000) / 100}%`, {
      x: 350,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    });

    page.drawText(`${Math.round(assetClass.retCont * 10000) / 100}%`, {
      x: 450,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    });
  });

  // Region Page:
  y -= 2 * spacing;

  page.drawText('Holdings and Risk Exposure Analysis', {
    x: 50,
    y,
    size: fontSizeTitle,
    color: rgb(0, 0, 0),
    font: boldFont,
  });

  y -= spacing;
  page.drawText('By Region', {
    x: 50,
    y,
    size: fontSizeSubtitle,
    color: rgb(0, 0, 0),
    font,
  });

  y -= 1.5 * spacing;
  page.drawText('Region', {
    x: 50,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font: boldFont,
  });
  page.drawText('Holdings Breakdown', {
    x: 350,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font: boldFont,
  });
  page.drawText('Risk Exposure', {
    x: 450,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font: boldFont,
  });

  [...Object.keys(data.domicile), 'Port. Specific Risk']
    .reduce((total, key) => {
      total.push({
        region: key === 'Other' ? 'Frontier Markets' : key,
        holdingsBreakdown: data.domicile[key],
        riskExposure: data.riskExp ? data.riskExp.C[key] : null,
      });

      return total;
    }, [])
    .forEach((item) => {
      page.drawRectangle({
        x: 40,
        y: y - 0.5 * spacing,
        width: width - 80,
        height: 0,
        borderColor: rgb(0, 0, 0),
        borderWidth: 0.5,
      });

      y -= 1.5 * spacing;
      page.drawText(`${item.region}`, {
        x: 50,
        y,
        size: fontSizeBody,
        color: rgb(0, 0, 0),
        font,
      });
      page.drawText(
        `${
          Math.round(
            (item.holdingsBreakdown === undefined
              ? 0
              : item.holdingsBreakdown) * 10000
          ) / 100
        }%`,
        {
          x: 350,
          y,
          size: fontSizeBody,
          color: rgb(0, 0, 0),
          font,
        }
      );
      page.drawText(
        `${
          Math.round(
            (item.riskExposure === undefined ? 0 : item.riskExposure) * 10000
          ) / 100
        }%`,
        {
          x: 450,
          y,
          size: fontSizeBody,
          color: rgb(0, 0, 0),
          font,
        }
      );
    });

  // Individual Page:

  const { tickersValid, riskCont } = data;
  const sorted = Object.keys(tickersValid).sort(
    (a, b) => riskCont.asset[b] - riskCont.asset[a]
  );

  const individualData = sorted.reduce((total, key) => {
    total.push({
      ticker: { ticker: key, name: getName(key, data) },
      assetAllocation: data.tickersValid[key].weight,
      riskCont: data.riskCont.asset[key],
      retCont: data.retCont.asset[key],
    });

    return total;
  }, []);

  page = pdfDoc.addPage(PageSizes.Letter);
  y = height - 50;
  page.drawText('Risk Contribution Analysis', {
    x: 50,
    y,
    size: fontSizeTitle,
    color: rgb(0, 0, 0),
    font: boldFont,
  });

  y -= spacing;
  page.drawText('By Individual Asset', {
    x: 50,
    y,
    size: fontSizeSubtitle,
    color: rgb(0, 0, 0),
    font: boldFont,
  });

  y -= 2 * spacing;
  page.drawText('Asset', {
    x: 50,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font: boldFont,
  });
  page.drawText('Asset Allocation', {
    x: 250,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font: boldFont,
  });
  page.drawText('Risk Contribution', {
    x: 350,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font: boldFont,
  });
  page.drawText('Return Contribution', {
    x: 450,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font: boldFont,
  });

  page.drawRectangle({
    x: 40,
    y: y - 0.75 * spacing,
    width: width - 80,
    height: 0,
    borderColor: rgb(0, 0, 0),
    borderWidth: 0.5,
  });

  y -= 2 * spacing;

  individualData.forEach((item) => {
    page.drawRectangle({
      x: 40,
      y: y - 1.75 * spacing,
      width: width - 80,
      height: 0,
      borderColor: rgb(0, 0, 0),
      borderWidth: 0.5,
    });

    page.drawText(`${item.ticker.ticker}`, {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    });
    page.drawText(`${item.ticker.name}`, {
      x: 50,
      y: y - spacing,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    });
    page.drawText(`${Math.round(item.assetAllocation * 10000) / 100}%`, {
      x: 250,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    });
    page.drawText(`${Math.round(item.riskCont * 10000) / 100}%`, {
      x: 350,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    });
    page.drawText(`${Math.round(item.retCont * 10000) / 100}%`, {
      x: 450,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    });
    y -= 3 * spacing;
    if (y < 50) {
      page = pdfDoc.addPage(PageSizes.Letter);
      y = height - 50;
    }
  });

  // Global Stock Sector Page:
  const { sectorAllocation } = data;
  const portfolioSectors = sectorAllocation.portfolio;
  const benchmarkSectors = sectorAllocation[benchmark]; // use the benchmark chosen by the user
  const globalTableData = getTableData(data.riskExp['S.EQ']);
  globalTableData.sort((a, b) => {
    if (a.ticker === 'Port. Specific Risk') return 1;
    if (b.ticker === 'Port. Specific Risk') return -1;
    return 0;
  });

  page = pdfDoc.addPage(PageSizes.Letter);
  y = height - 50;

  page.drawText('Holdings and Risk Exposure Analysis', {
    x: 50,
    y,
    size: fontSizeTitle,
    color: rgb(0, 0, 0),
    font: boldFont,
  });

  y -= spacing;
  page.drawText('By Global Stock Sector', {
    x: 50,
    y,
    size: fontSizeSubtitle,
    color: rgb(0, 0, 0),
    font,
  });

  y -= 1.5 * spacing;
  page.drawText('Holdings Breakdown', {
    x: 50,
    y,
    size: fontSizeSubtitle,
    color: rgb(0, 0, 0),
    font,
  });
  y -= spacing;

  page.drawText('Stock Sub-Portfolio', {
    x: 50,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font,
  });
  y -= spacing;

  page.drawText(
    'This breakdown is the value-weighted count of the stock holdings in each global stock sector within',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );
  y -= spacing;
  page.drawText(
    'your stock sub-portfolio (or your total portfolio if you have no other asset class).',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );

  y -= 1.5 * spacing;
  page.drawText('Sector', {
    x: 50,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font: boldFont,
  });
  page.drawText('Holdings', {
    x: 300,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font: boldFont,
  });
  page.drawText('Benchmark', {
    x: 400,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font: boldFont,
  });
  page.drawText('Difference', {
    x: 500,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font: boldFont,
  });

  Object.keys(portfolioSectors).forEach((sector) => {
    page.drawRectangle({
      x: 40,
      y: y - 0.5 * spacing,
      width: width - 80,
      height: 0,
      borderColor: rgb(0, 0, 0),
      borderWidth: 0.5,
    });

    y -= 1.5 * spacing;
    page.drawText(
      sector === 'Consumer Disc.' ? 'Consumer Discretionary' : sector,
      {
        x: 50,
        y,
        size: fontSizeBody,
        color: rgb(0, 0, 0),
        font,
      }
    );
    page.drawText(
      `${getTwoDecimal(portfolioSectors[sector], 'percentage') || '-'}`,
      {
        x: 300,
        y,
        size: fontSizeBody,
        color: rgb(0, 0, 0),
        font,
      }
    );
    page.drawText(
      `${getTwoDecimal(benchmarkSectors[sector], 'percentage') || '-'}`,
      {
        x: 400,
        y,
        size: fontSizeBody,
        color: rgb(0, 0, 0),
        font,
      }
    );
    page.drawText(
      `${
        getTwoDecimal(
          getDifference(portfolioSectors[sector], benchmarkSectors[sector]),
          'percentage'
        ) || '-'
      }`,
      {
        x: 500,
        y,
        size: fontSizeBody,
        color: rgb(0, 0, 0),
        font,
      }
    );
  });

  y -= 1.5 * spacing;
  page.drawText('Risk Exposure', {
    x: 50,
    y,
    size: fontSizeSubtitle,
    color: rgb(0, 0, 0),
    font: boldFont,
  });
  y -= spacing;

  page.drawText(
    'Stock Sub-Portfolio (excluding Balanced/Asset Allocation Funds)',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );
  y -= spacing;

  page.drawText(
    'These exposures are estimated using statistical analysis. They reflect the volatility and the diversification effects of your stock sub-portfolio.',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );
  y -= spacing;
  page.drawText(
    'It provides a different picture from holdings breakdown, as the latter does not take into account the fact that some companies may hedge their sector risk,',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );
  y -= spacing;
  page.drawText(
    'or have operations in more than one sector. Importantly, certain sector risks may be reduced or diversified away if you hold multiple assets in your portfolio.',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );

  y -= 1.5 * spacing;
  page.drawText('Global Stock Sector', {
    x: 50,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font: boldFont,
  });
  page.drawText('Sector Risk Exposure', {
    x: 250,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font: boldFont,
  });
  globalTableData.forEach((item) => {
    page.drawRectangle({
      x: 40,
      y: y - 0.5 * spacing,
      width: width - 80,
      height: 0,
      borderColor: rgb(0, 0, 0),
      borderWidth: 0.5,
    });

    y -= 1.5 * spacing;
    page.drawText(
      item.ticker === 'Consumer Disc.'
        ? 'Consumer Discretionary'
        : item.ticker === 'Port. Specific Risk'
        ? 'Portfolio Specific Risk'
        : item.ticker,
      {
        x: 50,
        y,
        size: fontSizeBody,
        color: rgb(0, 0, 0),
        font,
      }
    );
    page.drawText(`${getTwoDecimal(item.value, 'percentage') || '-'}`, {
      x: 250,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    });
  });

  page = pdfDoc.addPage(PageSizes.Letter);
  y = height - 50;
  page.drawText('Risk Exposure', {
    x: 50,
    y,
    size: fontSizeSubtitle,
    color: rgb(0, 0, 0),
    font: boldFont,
  });
  y -= spacing;

  page.drawText('Total Portfolio', {
    x: 50,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font,
  });
  y -= spacing;

  page.drawText(
    'These exposures are estimated using statistical analysis. They reflect the volatility and the diversification effects of your total portfolio.',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );
  y -= spacing;
  page.drawText(
    'If you have bonds or alternative assets in your portfolio, these figures will be different from those in the previous table,',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );
  y -= spacing;
  page.drawText(
    'as additional global stock sector risk may be added or diversified away.',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );

  y -= spacing;
  page.drawText('Global Stock Sector', {
    x: 50,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font: boldFont,
  });
  page.drawText('Sector Risk Exposure', {
    x: 250,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font: boldFont,
  });

  getTableData(data.riskExp.S)
    .sort((a, b) => {
      if (a.ticker === 'Port. Specific Risk') return 1;
      if (b.ticker === 'Port. Specific Risk') return -1;
      return 0;
    })
    .forEach((item) => {
      page.drawRectangle({
        x: 40,
        y: y - 0.5 * spacing,
        width: width - 80,
        height: 0,
        borderColor: rgb(0, 0, 0),
        borderWidth: 0.5,
      });

      y -= 1.5 * spacing;
      page.drawText(
        item.ticker === 'Consumer Disc.'
          ? 'Consumer Discretionary'
          : item.ticker === 'Port. Specific Risk'
          ? 'Portfolio Specific Risk'
          : item.ticker,
        {
          x: 50,
          y,
          size: fontSizeBody,
          color: rgb(0, 0, 0),
          font,
        }
      );
      page.drawText(`${getTwoDecimal(item.value, 'percentage') || '-'}`, {
        x: 250,
        y,
        size: fontSizeBody,
        color: rgb(0, 0, 0),
        font,
      });
    });

  // Macro Factor Page:

  const macroData = Object.keys(data.riskExp.F).reduce((total, key) => {
    total.push({
      ticker: key,
      macroRiskExp: data.riskExp.F[key],
    });

    return total;
  }, []);

  y -= 3 * spacing;
  page.drawText('Risk Exposure Analysis', {
    x: 50,
    y,
    size: fontSizeTitle,
    color: rgb(0, 0, 0),
    font: boldFont,
  });

  y -= spacing;
  page.drawText('By Macroeconomic Factor', {
    x: 50,
    y,
    size: fontSizeSubtitle,
    color: rgb(0, 0, 0),
    font,
  });

  y -= spacing;
  page.drawText(
    'This section shows your portfolio’s risk attribution to seven macroeconomic factors.',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );

  y -= 2 * spacing;
  page.drawText('Macroeconomic Risk Exposure', {
    x: 50,
    y,
    size: fontSizeSubtitle,
    color: rgb(0, 0, 0),
    font: boldFont,
  });

  y -= 2 * spacing;
  page.drawText('Asset', {
    x: 50,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font: boldFont,
  });
  page.drawText('Macroeconomic Risk Exposure', {
    x: 250,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font: boldFont,
  });
  getTableData(macroData).forEach((item) => {
    page.drawRectangle({
      x: 40,
      y: y - 0.5 * spacing,
      width: width - 80,
      height: 0,
      borderColor: rgb(0, 0, 0),
      borderWidth: 0.5,
    });

    y -= 1.5 * spacing;
    page.drawText(
      item.value.ticker === 'Consumer Disc.'
        ? 'Consumer Discretionary'
        : item.value.ticker === 'Port. Specific Risk'
        ? 'Portfolio Specific Risk'
        : item.value.ticker,
      {
        x: 50,
        y,
        size: fontSizeBody,
        color: rgb(0, 0, 0),
        font,
      }
    );
    page.drawText(
      `${getTwoDecimal(item.value.macroRiskExp, 'percentage') || '-'}`,
      {
        x: 250,
        y,
        size: fontSizeBody,
        color: rgb(0, 0, 0),
        font,
      }
    );
  });

  // Equity Style Page:

  const equityStyle = { ...data.partialCorr['G.EQ'] };
  delete equityStyle.ignoredNegFac;
  const { priceMultiples } = data;
  const portfolioMultiples = priceMultiples.portfolio;
  const benchmarkMultiples = priceMultiples[benchmark]; // use the benchmark chosen by the user

  page = pdfDoc.addPage(PageSizes.Letter);
  y = height - 50;

  page.drawText('Portfolio-Level Style Analysis', {
    x: 50,
    y,
    size: fontSizeTitle,
    color: rgb(0, 0, 0),
    font: boldFont,
  });

  y -= spacing;
  page.drawText('By Equity Style', {
    x: 50,
    y,
    size: fontSizeSubtitle,
    color: rgb(0, 0, 0),
    font,
  });

  y -= 2 * spacing;
  page.drawText('Style Factors', {
    x: 50,
    y,
    size: fontSizeSubtitle,
    color: rgb(0, 0, 0),
    font: boldFont,
  });
  y -= spacing;
  page.drawText(
    "This section shows your portfolio's equity style tilts using the Fama-French-Carhart equity style factors: market, size, value, and",
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );
  y -= spacing;
  page.drawText(
    'momentum. In the table, portfolio-level style tilts are assessed using the partial correlation between your equity portfolio returns',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );
  y -= spacing;
  page.drawText(
    'and each factor. If it is positive (and statistically significant), then your equity portfolio has exposure to the market, small size, value, and momentum.',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );
  y -= 2 * spacing;
  page.drawText(
    'If it is negative, then your equity portfolio is hedged to the market, and has exposure to large size, growth, and reversal.',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );
  y -= spacing;
  page.drawText(
    'Further, since the partial correlation lies between -1 and 1, its magnitude shows the strength of the correlation.',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );

  y -= 2 * spacing;
  page.drawText('Style Factor', {
    x: 50,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font: boldFont,
  });
  page.drawText('Partial Correlation between Portfolio and Factor', {
    x: 150,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font: boldFont,
  });
  page.drawText('Style Tilt', {
    x: 400,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font: boldFont,
  });

  Object.keys(equityStyle).forEach((factor) => {
    page.drawRectangle({
      x: 40,
      y: y - 0.5 * spacing,
      width: width - 80,
      height: 0,
      borderColor: rgb(0, 0, 0),
      borderWidth: 0.5,
    });

    y -= 1.5 * spacing;
    page.drawText(`${factor}`, {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    });
    page.drawText(
      typeof equityStyle[factor] === 'number'
        ? getTwoDecimal(equityStyle[factor]) !== null
          ? `${getTwoDecimal(equityStyle[factor])}`
          : '-'
        : '-',
      {
        x: 150,
        y,
        size: fontSizeBody,
        color: rgb(0, 0, 0),
        font,
      }
    );
    page.drawText(
      tiltLabels[factor]
        ? equityStyle[factor] < 0
          ? tiltLabels[factor].negative
          : equityStyle[factor] === 0
          ? 'Neutral'
          : tiltLabels[factor].positive
        : '-',
      {
        x: 400,
        y,
        size: fontSizeBody,
        color: rgb(0, 0, 0),
        font,
      }
    );
  });

  y -= 2 * spacing;
  page.drawText('Interpretation Guide', {
    x: 50,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font: boldFont,
  });

  y -= spacing;
  page.drawText(
    '- Values close to 1 indicate strong positive correlation with the factor',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );
  y -= spacing;
  page.drawText(
    '- Values close to -1 indicate strong negative correlation with',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );
  y -= spacing;
  page.drawText(
    '- “-” indicates there is no significant correlation with the factor',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );

  y -= 2 * spacing;
  page.drawText('Price Multiples', {
    x: 50,
    y,
    size: fontSizeSubtitle,
    color: rgb(0, 0, 0),
    font: boldFont,
  });
  y -= spacing;
  page.drawText(
    'Price multiples help assess how "expensive" or "cheap" your stock portfolio is by looking at stock prices per dollar of a key financial metric.',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );
  y -= spacing;
  page.drawText(
    'We display below the average price (P) to earnings (E), cash flow (CF), book (B), and sales (S) ratios for your portfolio and for the benchmark.',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );
  y -= 2 * spacing;
  page.drawText(
    "Note that the P/B ratio relates to the value factor above. Comparing your portfolio's P/B ratio to the benchmark's helps determine if",
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );
  y -= spacing;
  page.drawText(
    'your portfolio leans toward value investing (lower P/B) or growth investing (higher P/B).',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );

  y -= 2 * spacing;
  page.drawText('P/E Ratio', {
    x: 150,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font: boldFont,
  });
  page.drawText('P/CF Ratio', {
    x: 250,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font: boldFont,
  });
  page.drawText('P/B Ratio', {
    x: 350,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font: boldFont,
  });
  page.drawText('P/Sales', {
    x: 450,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font: boldFont,
  });
  page.drawRectangle({
    x: 40,
    y: y - 0.5 * spacing,
    width: width - 80,
    height: 0,
    borderColor: rgb(0, 0, 0),
    borderWidth: 0.5,
  });

  y -= 1.5 * spacing;
  page.drawText('My Portfolio', {
    x: 50,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font,
  });
  page.drawText(
    `${getTwoDecimal(portfolioMultiples.price_to_earnings)}` || '-',
    {
      x: 150,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );

  page.drawText(
    `${getTwoDecimal(portfolioMultiples.price_to_cashflow)}` || '-',
    {
      x: 250,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );
  page.drawText(`${getTwoDecimal(portfolioMultiples.price_to_book)}` || '-', {
    x: 350,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font,
  });
  page.drawText(`${getTwoDecimal(portfolioMultiples.price_to_sales)}` || '-', {
    x: 450,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font,
  });
  page.drawRectangle({
    x: 40,
    y: y - 0.5 * spacing,
    width: width - 80,
    height: 0,
    borderColor: rgb(0, 0, 0),
    borderWidth: 0.5,
  });

  y -= 1.5 * spacing;
  page.drawText(benchmarkMultiples.name || 'Benchmark', {
    x: 50,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font,
  });
  page.drawText(getTwoDecimal(benchmarkMultiples.price_to_earnings) || '-', {
    x: 150,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font,
  });

  page.drawText(getTwoDecimal(benchmarkMultiples.price_to_cashflow) || '-', {
    x: 250,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font,
  });
  page.drawText(getTwoDecimal(benchmarkMultiples.price_to_book) || '-', {
    x: 350,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font,
  });
  page.drawText(getTwoDecimal(benchmarkMultiples.price_to_sales) || '-', {
    x: 450,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font,
  });

  // ESG Page:
  if (analytics.esgToggle) {
    const { ws_grade } = esg.portfolio;
    const tickers = Object.keys(esg)
      .filter((t) => t !== 'portfolio')
      .sort((a, b) => Object.keys(esg[b]).length - Object.keys(esg[a]).length);
    page = pdfDoc.addPage(PageSizes.Letter);
    y = height - 50;

    page.drawText(
      `Environmental | Social | Governance (ESG): ${
        ws_grade[0] ? `${ws_grade[0]}%` : null
      } ${ws_grade[1]}`,
      {
        x: 50,
        y,
        size: fontSizeTitle,
        color: rgb(0, 0, 0),
        font: boldFont,
      }
    );

    y -= spacing;

    page.drawText('ESG data is powered by OWL ESG.', {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    });

    y -= spacing;

    page.drawText(
      'OWL employs a big data approach to ESG metrics and sustainable investing. They incorporate data from a variety of sources',
      {
        x: 50,
        y,
        size: fontSizeBody,
        color: rgb(0, 0, 0),
        font,
      }
    );

    y -= spacing;

    page.drawText(
      'including corporate disclosures and filings, news sources, NGOs, research firms, and more. OWL’s consensus modelling optimizes multiple',
      {
        x: 50,
        y,
        size: fontSizeBody,
        color: rgb(0, 0, 0),
        font,
      }
    );

    y -= spacing;

    page.drawText(
      'viewpoints to avoid subjective scoring. Mutual fund and ETF data is aggregated from individual holdings’ scores.',
      {
        x: 50,
        y,
        size: fontSizeBody,
        color: rgb(0, 0, 0),
        font,
      }
    );

    y -= 2 * spacing;

    page.drawText(
      'How should you interpret the ESG data below? There are two pieces of information you should consider for each category: a raw OWL score on',
      {
        x: 50,
        y,
        size: fontSizeBody,
        color: rgb(0, 0, 0),
        font,
      }
    );

    y -= spacing;

    page.drawText(
      "a 0-100 scale, and a percentile that compares the score of a stock or fund to its peers'. The former is an absolute number, and the latter",
      {
        x: 50,
        y,
        size: fontSizeBody,
        color: rgb(0, 0, 0),
        font,
      }
    );

    y -= spacing;

    page.drawText(
      'is a relative standing. In both cases, a higher number is better. The overall Wealthscope grade at the top considers the tight distribution',
      {
        x: 50,
        y,
        size: fontSizeBody,
        color: rgb(0, 0, 0),
        font,
      }
    );

    y -= spacing;

    page.drawText(
      'of the OWL scores for funds (see chart below), if you have any in your portfolio.',
      {
        x: 50,
        y,
        size: fontSizeBody,
        color: rgb(0, 0, 0),
        font,
      }
    );

    y -= 2 * spacing;

    page.drawText(
      `Percentage of holdings with ESG scores by weight: ${(
        esg.portfolio.weight * 100
      ).toFixed(2)}% `,
      {
        x: 50,
        y,
        size: fontSizeBody,
        color: rgb(0, 0, 0),
        font,
      }
    );

    y -= 2 * spacing;

    page.drawText('Individual ESG Scores', {
      x: 50,
      y,
      size: fontSizeSubtitle,
      color: rgb(0, 0, 0),
      font: boldFont,
    });

    y -= spacing;

    page.drawText(
      'Regional sector percentiles for stocks; Asset class percentiles for funds',
      {
        x: 50,
        y,
        size: fontSizeBody,
        color: rgb(0, 0, 0),
        font,
      }
    );

    y -= spacing;

    page.drawText('Name', {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font: boldFont,
    });

    page.drawText('Weight', {
      x: 250,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font: boldFont,
    });

    page.drawText('ESG', {
      x: 325,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font: boldFont,
    });

    page.drawText('E', {
      x: 400,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font: boldFont,
    });

    page.drawText('S', {
      x: 475,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font: boldFont,
    });

    page.drawText('G', {
      x: 550,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font: boldFont,
    });
    page.drawRectangle({
      x: 40,
      y: y - 0.75 * spacing,
      width: width - 80,
      height: 0,
      borderColor: rgb(0, 0, 0),
      borderWidth: 0.5,
    });

    y -= 2 * spacing;

    tickers.forEach((ticker) => {
      const esgData = esg[ticker];
      const {
        esg_score,
        esg_percentile,
        e_score,
        e_percentile,
        s_score,
        s_percentile,
        g_score,
        g_percentile,
      } = esgData;

      page.drawText(`${esgData.name || ticker}`, {
        x: 50,
        y,
        size: fontSizeBody,
        color: rgb(0, 0, 0),
        font,
      });

      page.drawText(`${(esgData.weight * 100).toFixed(2)}%`, {
        x: 250,
        y,
        size: fontSizeBody,
        color: rgb(0, 0, 0),
        font,
      });

      if (esg_score) {
        page.drawText(`${esg_score}`, {
          x: 325,
          y,
          size: fontSizeBody,
          color: rgb(0, 0, 0),
          font,
        });
        page.drawText(
          esg_percentile ? `${(esg_percentile * 100).toFixed(0)}%` : '',
          {
            x: 325,
            y: y - spacing,
            size: fontSizeBody,
            color: rgb(0, 0, 0),
            font,
          }
        );
      } else {
        page.drawText('-', {
          x: 325,
          y,
          size: fontSizeBody,
          color: rgb(0, 0, 0),
          font,
        });
      }

      if (e_score) {
        page.drawText(`${e_score}`, {
          x: 400,
          y,
          size: fontSizeBody,
          color: rgb(0, 0, 0),
          font,
        });
        page.drawText(
          e_percentile ? `${(e_percentile * 100).toFixed(0)}%` : '',
          {
            x: 400,
            y: y - spacing,
            size: fontSizeBody,
            color: rgb(0, 0, 0),
            font,
          }
        );
      } else {
        page.drawText('-', {
          x: 400,
          y,
          size: fontSizeBody,
          color: rgb(0, 0, 0),
          font,
        });
      }

      if (s_score) {
        page.drawText(`${s_score}`, {
          x: 475,
          y,
          size: fontSizeBody,
          color: rgb(0, 0, 0),
          font,
        });
        page.drawText(
          s_percentile ? `${(s_percentile * 100).toFixed(0)}%` : '',
          {
            x: 475,
            y: y - spacing,
            size: fontSizeBody,
            color: rgb(0, 0, 0),
            font,
          }
        );
      } else {
        page.drawText('-', {
          x: 475,
          y,
          size: fontSizeBody,
          color: rgb(0, 0, 0),
          font,
        });
      }

      if (g_score) {
        page.drawText(`${g_score}`, {
          x: 550,
          y,
          size: fontSizeBody,
          color: rgb(0, 0, 0),
          font,
        });
        page.drawText(
          g_percentile ? `${(g_percentile * 100).toFixed(0)}%` : '',
          {
            x: 550,
            y: y - spacing,
            size: fontSizeBody,
            color: rgb(0, 0, 0),
            font,
          }
        );
      } else {
        page.drawText('-', {
          x: 550,
          y,
          size: fontSizeBody,
          color: rgb(0, 0, 0),
          font,
        });
      }
      page.drawRectangle({
        x: 40,
        y: y - 1.75 * spacing,
        width: width - 80,
        height: 0,
        borderColor: rgb(0, 0, 0),
        borderWidth: 0.5,
      });

      y -= 3 * spacing;
      if (y < 50) {
        page = pdfDoc.addPage(PageSizes.Letter);
        y = height - 50;
      }
    });
  }

  // Footnotes Page:

  page = pdfDoc.addPage(PageSizes.Letter);
  y = height - 50;
  page.drawText('Footnotes', {
    x: 50,
    y,
    size: fontSizeTitle,
    color: rgb(0, 0, 0),
    font: boldFont,
  });

  y -= spacing;
  page.drawText('1. Wealthscope Portfolio Scorecard guidelines:', {
    x: 50,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font,
  });
  y -= spacing;
  page.drawText(
    '  To protect the integrity of the statistical analysis, Wealthscope implements the following rules when analyzing your portfolio:',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );

  y -= spacing;
  page.drawText(
    '  - If all of the securities in the portfolio have more than or equal to 48 months of history, all of the analysis will be performed.',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );
  y -= spacing;
  page.drawText('    Otherwise, we exclude the following securities:', {
    x: 50,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font,
  });

  y -= spacing;
  page.drawText('     - Securities that have less than 36 months of history;', {
    x: 50,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font,
  });

  y -= spacing;
  page.drawText(
    '     - Securities that have between 36 and 47 months of history and collectively weigh less than 10% of the portfolio.',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );
  y -= spacing;
  page.drawText(
    '  - For securities included in the analysis, the period of analysis will be the longest common history.',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );

  y -= spacing;
  page.drawText(
    '  - If all of the securities in the portfolio have less than 36 months of history, we provide only basic summary information for',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );

  y -= spacing;
  page.drawText('    the portfolio using the longest common history.', {
    x: 50,
    y,
    size: fontSizeBody,
    color: rgb(0, 0, 0),
    font,
  });

  y -= spacing;
  page.drawText(
    '  - If a passive ETF has insufficient history but there is a close substitute, the latter will be used in the analysis. For some',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );
  y -= spacing;
  page.drawText(
    '    passive ETFs with short history, we may use the index returns (minus the MER) prior to inception.',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );

  y -= 2 * spacing;
  page.drawText(
    '2. The Overall Score is the average score of the individual dimensions. Each individual score is out of 100 for the specific',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );
  y -= spacing;
  page.drawText(
    'time period in question. A higher score always means a more desirable characteristic.',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );

  y -= 2 * spacing;
  page.drawText(
    `3. All key statistics are reported in ${decideCurrency(
      analytics.data,
      localStorage.getItem('user_region') || 'CA'
    )}`,
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );

  y -= 2 * spacing;
  page.drawText(
    '4. The benchmark for your portfolio is automatically selected depending on your asset allocation. It represents a simple, low-cost',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );
  y -= spacing;
  page.drawText(
    'two-ETF (one broad equity and one broad fixed income) alternative for your portfolio.',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );

  y -= 2 * spacing;
  page.drawText(
    '5. The following are risk-adjusted return ratios, and a higher ratio is better: The Sharpe Ratio measures the portfolio’s reward (excess',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );
  y -= spacing;
  page.drawText(
    'returns over the risk-free rate) per unit of risk. The Roy’s Safety First Criterion measures the portfolio’s reward (excess returns over',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );
  y -= spacing;
  page.drawText(
    'the target rate) per unit of risk. The Sortino Ratio measures the portfolio’s reward (excess returns over the target rate) per unit of downside risk.',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );

  y -= 2 * spacing;
  page.drawText(
    '6. The following are portfolio risk measures: Total risk represents the volatility of the portfolio’s returns as measured by its standard deviation. Downside',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );
  y -= spacing;
  page.drawText(
    'risk is the volatility of the portfolio’s return below a target rate. Maximum drawdown is the maximum peak to trough loss during a given period.',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );
  y -= spacing;
  page.drawText(
    'Downside capture measures whether the portfolio lost less than the market when there was a market downturn.',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );

  y -= 2 * spacing;
  page.drawText(
    '7. The default target rate is set to zero, representing nominal capital preservation.',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );

  y -= 2 * spacing;
  page.drawText(
    '8. Fees capture product fees if you have mutual funds/ETFs. It can include a trailing commission for your advisor, depending on the',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );

  y -= spacing;
  page.drawText(
    'share class you have (e.g., Class A). A weight fee of 3% will receive a score of 0%.',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );

  y -= 2 * spacing;
  page.drawText(
    '9. A weighted income (dividends and other distributions) yield of 5% or higher will receive a score of 100%.',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );

  y -= 2 * spacing;
  page.drawText(
    '10. Diversification is assessed using your portfolio’s average correlation and four other dimensions across i) asset class, ii)',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );

  y -= spacing;
  page.drawText(
    'geographical region, iii) business sectors, and iv) macroeconomic factors.',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );

  y -= 2 * spacing;
  page.drawText(
    '11. For regional stock market, global stock sector, macroeconomic, and equity style diversification, we show you your portfolio’s risk',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );
  y -= spacing;
  page.drawText(
    'exposures. The latter measures how much the volatility of your portfolio can be attributed to each factor. Portfolio-specific risk',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );
  y -= spacing;
  page.drawText(
    'is the portion of your portfolio’s volatility that cannot be explained by the factors in question.',
    {
      x: 50,
      y,
      size: fontSizeBody,
      color: rgb(0, 0, 0),
      font,
    }
  );

  if (Object.keys(esg).length) {
    y -= 2 * spacing;
    page.drawText(
      '12. The OWL ESG scores are on a 0-100 scale, with a higher score being more desirable. The Wealthscope grading scheme differs from',
      {
        x: 50,
        y,
        size: fontSizeBody,
        color: rgb(0, 0, 0),
        font,
      }
    );
    y -= spacing;
    page.drawText(
      'the ESG scores as the grading scheme reflects the distribution of the scores, which is tight around the middle for funds (see the distribution chart).',
      {
        x: 50,
        y,
        size: fontSizeBody,
        color: rgb(0, 0, 0),
        font,
      }
    );
  }

  const pdfBytes = await pdfDoc.save();
  const blob = new Blob([pdfBytes], { type: 'application/pdf' });
  const file = new File([blob], 'document.pdf', { type: 'application/pdf' });

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;

  const formData = new FormData();
  formData.append('file', file);
  formData.append('purpose', 'user_data');

  //   a.download = 'RiskProfileQuestionnaire.pdf';
  document.body.appendChild(a);
  // a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);

  return formData;
}
